// src/pages/admin/tabs/parts/UnitsPanel.jsx

import React from 'react';
import { AdminAPI } from '../../../../api/admin';
import { normalizeMediaUrl } from '../../../../api/base';
import { Card, Row2, Field, Actions, Btn, Badge, TableWrap, Table } from '../../ui';
import MediaPickerModal from '../../components/MediaPickerModal';

const nonEmpty = (x)=> x!=null && String(x).trim()!=='';

export default function UnitsPanel({ propertyId, propertyType = 'regular' }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignUnits,
          create: AdminAPI.createForeignUnit,
          update: AdminAPI.updateForeignUnit,
          delete: AdminAPI.deleteForeignUnit,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanUnits,
          create: AdminAPI.createSuburbanUnit,
          update: AdminAPI.updateSuburbanUnit,
          delete: AdminAPI.deleteSuburbanUnit,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentUnits,
          create: AdminAPI.createCommercialRentUnit,
          update: AdminAPI.updateCommercialRentUnit,
          delete: AdminAPI.deleteCommercialRentUnit,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingUnits,
          create: AdminAPI.createParkingUnit,
          update: AdminAPI.updateParkingUnit,
          delete: AdminAPI.deleteParkingUnit,
        };
      default:
        return {
          list: AdminAPI.listUnits,
          create: AdminAPI.createUnit,
          update: AdminAPI.updateUnit,
          delete: AdminAPI.deleteUnit,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  // форма добавления юнита
  const [form, setForm] = React.useState({
    title_ru: '', title_en: '', title_zh: '', unit_no: '',
    floor: '', bedrooms: '', bathrooms: '',
    area_total: '', balcony_area: '',
    price: '', price_currency: 'AED',
    status: 'available', is_featured: false, is_active: true,
    plan_media_id: undefined,
  });

  // план юнита (картинка)
  const [planPreview, setPlanPreview] = React.useState('');
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const [uploadBusy, setUploadBusy] = React.useState(false);
  // файлы, подготовленные к привязке после создания юнита
  const [pendingMedia, setPendingMedia] = React.useState([]); // [{id, thumbnail_url, url}]

  // пакетное добавление юнитов
  const [batch, setBatch] = React.useState([]); // список подготовленных к созданию юнитов (payload'ы)

  const load = React.useCallback(async ()=>{
    setLoading(true); setErr(''); setOk('');
    try{
      const d = await api.list(propertyId, { page_size: 200, locale: 'ru', currency: 'AED' });
      setItems(d?.items || d || []); // Поддерживаем разные форматы ответов
    }catch(e){ setErr(e?.message || 'Не удалось загрузить планировки'); }
    finally{ setLoading(false); }
  }, [propertyId, api]);

  React.useEffect(()=>{ if(propertyId) load(); }, [propertyId, load]);

  const setF = (k,v)=> setForm(f=>({ ...f, [k]: v }));

  const buildPayload = ()=>{
    // Не требуем обязательно title, можно создать юнит только с unit_no
    return {
      title_ru: nonEmpty(form.title_ru) ? form.title_ru : undefined,
      title_en: nonEmpty(form.title_en) ? form.title_en : undefined,
      title_zh: nonEmpty(form.title_zh) ? form.title_zh : undefined,
      unit_no: nonEmpty(form.unit_no) ? form.unit_no : undefined,
      unit_number: nonEmpty(form.unit_no) ? form.unit_no : undefined, // Дублируем для совместимости
      floor: form.floor!=='' ? Number(form.floor) : undefined,
      bedrooms: form.bedrooms!=='' ? Number(form.bedrooms) : undefined,
      bathrooms: form.bathrooms!=='' ? Number(form.bathrooms) : undefined,
      area_total: form.area_total!=='' ? Number(form.area_total) : undefined,
      balcony_area: form.balcony_area!=='' ? Number(form.balcony_area) : undefined,
      price: form.price!=='' ? Number(form.price) : undefined,
      price_currency: form.price ? (form.price_currency || 'AED') : undefined,
      status: form.status || 'available',
      is_featured: !!form.is_featured,
      is_active: !!form.is_active,
      plan_media_id: form.plan_media_id ?? undefined,
    };
  };

  const resetForm = ()=>{
    setForm({
      title_ru:'', title_en:'', title_zh:'', unit_no:'',
      floor:'', bedrooms:'', bathrooms:'',
      area_total:'', balcony_area:'',
      price:'', price_currency:'AED',
      status:'available', is_featured:false, is_active:true,
      plan_media_id: undefined,
    });
    setPlanPreview('');
    setPendingMedia([]);
  };

  const create = async ()=>{
    setErr(''); setOk('');
    try{
      const payload = buildPayload();
      const resp = await api.create(propertyId, payload);
      const unitId = resp?.id || resp?.unit?.id || resp?.unit_id;
      if (unitId && pendingMedia.length){
        try{
          let order = 0;
          for (let i=0;i<pendingMedia.length;i++){
            await AdminAPI.attachUnitMedia(unitId, { media_id: pendingMedia[i].id, sort_order: order++ });
          }
        }catch(_e){ /* необязательно критично */ }
      }
      setOk('Юнит создан');
      resetForm();
      await load();
    }catch(e){ setErr(e?.payload?.message || e?.message || 'Ошибка создания'); }
  };

  const addToBatch = ()=>{
    try{
      const payload = buildPayload();
      setBatch(list => [...list, payload]);
      resetForm();
      setOk('Добавлено в список');
    }catch(e){ setErr(e?.message || 'Проверьте форму'); }
  };

  const createBatch = async ()=>{
    if (!batch.length) return;
    setErr(''); setOk('');
    setLoading(true);
    try{
      for (let i=0;i<batch.length;i++){
        await AdminAPI.createUnit(propertyId, batch[i]);
      }
      setBatch([]);
      setOk('Планировки созданы');
      await load();
    }catch(e){ setErr(e?.message || 'Не удалось создать часть планировок'); }
    finally{ setLoading(false); }
  };

  // план: загрузить выбранные файлы (первый станет планом), все — в очередь для привязки
  const uploadPlan = async (fileOrList)=>{
    const files = Array.from((fileOrList && fileOrList.length!=null) ? fileOrList : [fileOrList]).filter(Boolean);
    if(!files.length) return;
    setUploadBusy(true); setErr('');
    try{
      const newItems = [];
      for (let i=0;i<files.length;i++){
        const up = await AdminAPI.uploadMedia(files[i]);
        const f  = up?.file || up;
        if(!f?.id) throw new Error('Медиафайл не вернул id');
        newItems.push({ id: f.id, thumbnail_url: f.thumbnail_url, url: f.url });
        if (i===0){
          setF('plan_media_id', f.id);
          setPlanPreview(f.thumbnail_url || f.url || '');
        }
      }
      setPendingMedia(prev => [...prev, ...newItems]);
      setOk(files.length>1 ? `Загружено файлов: ${files.length}` : 'План загружен');
    }catch(e){ setErr(e?.message || 'Не удалось загрузить файл(ы)'); }
    finally{
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const pickFromLib = (list)=>{
    const picked = Array.isArray(list) ? list : [];
    if (!picked.length) return;
    const first = picked[0];
    setF('plan_media_id', first.id);
    setPlanPreview(first.thumbnail_url || first.url || '');
    setPendingMedia(prev => [
      ...prev,
      ...picked.map(x => ({ id:x.id, thumbnail_url: x.thumbnail_url, url: x.url }))
    ]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const clearPlan = ()=>{ setF('plan_media_id', undefined); setPlanPreview(''); if(fileInputRef.current) fileInputRef.current.value=''; };

  // простые обновления юнита на месте
  const patchUnit = async (u, patch)=>{
    try{
      if (propertyType === 'regular') {
        await AdminAPI.updateUnit(u.id, patch);
      } else {
        await api.update(propertyId, u.id, patch);
      }
      await load();
    }catch(e){ alert(e?.message || 'Не удалось сохранить'); }
  };
  const delUnit = async (u)=>{
    if(!window.confirm('Удалить юнит?')) return;
    try{ 
      if (propertyType === 'regular') {
        await AdminAPI.deleteUnit(u.id);
      } else {
        await api.delete(propertyId, u.id);
      }
      await load(); 
    }
    catch(e){ alert(e?.message || 'Не удалось удалить'); }
  };

  return (
    <Card>
      <h3>Планировки {loading ? '(загрузка...)' : ''}</h3>
      {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
      {ok  && <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

      {/* Форма добавления */}
      <Card style={{background:'var(--card)', margin:'8px 0', padding:12}}>
        <h4>Добавить юнит</h4>
        <Row2>
          <Field>Название RU<input value={form.title_ru} onChange={e=>setF('title_ru', e.target.value)} /></Field>
          <Field>Название EN<input value={form.title_en} onChange={e=>setF('title_en', e.target.value)} /></Field>
        </Row2>
        <Field>Название ZH (中文)<input value={form.title_zh} onChange={e=>setF('title_zh', e.target.value)} /></Field>
        <Row2>
          <Field>Номер юнита<input value={form.unit_no} onChange={e=>setF('unit_no', e.target.value)} /></Field>
          <Field>Этаж<input type="number" value={form.floor} onChange={e=>setF('floor', e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Спальни<input type="number" value={form.bedrooms} onChange={e=>setF('bedrooms', e.target.value)} /></Field>
          <Field>Санузлы<input type="number" value={form.bathrooms} onChange={e=>setF('bathrooms', e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Площадь, м²<input type="number" value={form.area_total} onChange={e=>setF('area_total', e.target.value)} /></Field>
          <Field>Балкон, м²<input type="number" value={form.balcony_area} onChange={e=>setF('balcony_area', e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Цена
            <input type="number" value={form.price} onChange={e=>setF('price', e.target.value)} />
          </Field>
          <Field>Валюта
            <select value={form.price_currency} onChange={e=>setF('price_currency', e.target.value)}>
              <option value="RUB">RUB</option>
              <option value="AED">AED</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </Field>
        </Row2>
        <Row2>
          <Field>Статус
            <select value={form.status} onChange={e=>setF('status', e.target.value)}>
              <option value="available">available</option>
              <option value="reserved">reserved</option>
              <option value="sold">sold</option>
            </select>
          </Field>
          <Field>Активен
            <select value={form.is_active ? '1':'0'} onChange={e=>setF('is_active', e.target.value==='1')}>
              <option value="1">Да</option><option value="0">Нет</option>
            </select>
          </Field>
        </Row2>

        <h4 style={{marginTop:12}}>План юнита (опционально)</h4>
        {planPreview && (
          <div style={{marginBottom:8}}>
            <img alt="" src={planPreview} style={{width:120, height:120, objectFit:'cover', borderRadius:12, border:'1px solid var(--bdr)'}} />
            {form.plan_media_id && <div style={{fontSize:12, opacity:.7, marginTop:4}}>media_id: <b>{form.plan_media_id}</b></div>}
          </div>
        )}
        <Actions style={{gap:10, flexWrap:'wrap'}}>
          <input type="file" accept="image/*" multiple ref={fileInputRef} style={{display:'none'}} onChange={(e)=>uploadPlan(e.target.files)}/>
          <Btn onClick={()=>fileInputRef.current?.click()} disabled={uploadBusy}>{uploadBusy?'Загрузка…':'Загрузить выбранные'}</Btn>
          <Btn onClick={()=>setPickerOpen(true)} disabled={uploadBusy}>Выбрать из медиатеки</Btn>
          {planPreview && <Btn onClick={clearPlan}>Убрать</Btn>}
        </Actions>

        {!!pendingMedia.length && (
          <div style={{marginTop:10}}>
            <div style={{opacity:.8, marginBottom:6}}>Выбранные фото (привяжутся после создания): {pendingMedia.length}</div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(100px,1fr))', gap:10}}>
              {pendingMedia.map((m, idx)=> (
                <div key={`${m.id}-${idx}`} style={{border:'1px solid var(--bdr)', borderRadius:10, padding:6}}>
                  {(m.thumbnail_url || m.url)
                    ? <img alt="" src={m.thumbnail_url || m.url} style={{width:'100%', aspectRatio:'1/1', objectFit:'cover', borderRadius:6}}/>
                    : <div style={{height:80, display:'grid', placeItems:'center', opacity:.6}}>нет превью</div>}
                  <div style={{display:'flex', gap:6, alignItems:'center', marginTop:6}}>
                    <Btn onClick={()=>setPendingMedia(list=>list.filter((_,i)=>i!==idx))}>Удалить</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Actions style={{marginTop:12, gap:8, flexWrap:'wrap'}}>
          <Btn onClick={addToBatch}>+ В список</Btn>
          <Btn $kind="primary" onClick={create}>+ Добавить юнит</Btn>
          <div style={{flex:1}}/>
          <Btn onClick={createBatch} disabled={!batch.length}>Создать все ({batch.length})</Btn>
        </Actions>

        {!!batch.length && (
          <div style={{marginTop:8}}>
            <div style={{opacity:.75, marginBottom:6}}>К созданию: {batch.length}</div>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>№</th>
                    <th>Этаж</th>
                    <th>Спальни</th>
                    <th>Площадь</th>
                    <th>Цена</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {batch.map((b, idx)=> (
                    <tr key={idx}>
                      <td style={{maxWidth:220}}>{b.title_ru || b.title_en || '—'}</td>
                      <td>{b.unit_no || '—'}</td>
                      <td>{b.floor ?? '—'}</td>
                      <td>{b.bedrooms ?? '—'}</td>
                      <td>{b.area_total ?? '—'}</td>
                      <td>{b.price != null ? `${b.price} ${b.price_currency||''}` : '—'}</td>
                      <td><Btn onClick={()=>setBatch(list=>list.filter((_,i)=>i!==idx))}>Убрать</Btn></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </div>
        )}
      </Card>

      {/* Таблица юнитов */}
      <TableWrap style={{ overflowX: 'auto' }}>
        <Table style={{ minWidth: '1200px' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>№</th>
              <th>Этаж</th>
              <th>Площадь</th>
              <th>Цена</th>
              <th>Статус</th>
              <th>Активен</th>
              <th>План</th>
              <th>Управление планом</th>
              <th>Фото</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(u=>(
              <tr key={u.id}>
                <td>{u.id}</td>
                <td style={{maxWidth:220}}>
                  {(() => {
                    // Генерируем осмысленное название
                    if (u.title || u.title_ru || u.title_en) {
                      return u.title ?? u.title_ru ?? u.title_en;
                    }
                    
                    if (u.unit_no || u.unit_number) {
                      return `Юнит ${u.unit_no || u.unit_number}`;
                    }
                    
                    // Генерируем название на основе характеристик
                    const parts = [];
                    if (u.bedrooms) parts.push(`${u.bedrooms}-комн`);
                    if (u.area_total) parts.push(`${u.area_total}м²`);
                    if (u.floor) parts.push(`${u.floor} эт`);
                    
                    if (parts.length > 0) {
                      return `Юнит ${parts.join(', ')}`;
                    }
                    
                    return `Юнит #${u.id}`;
                  })()}
                </td>
                <td>{u.unit_no || u.unit_number || '—'}</td>
                <td>{u.floor ?? '—'}</td>
                <td>{u.area_total ?? '—'}</td>
                <td>{u.price != null ? `${u.price} ${u.price_currency || u.currency?.code || ''}` : (u.price_base ?? '—')}</td>
                <td>
                  <select value={u.status || 'available'} onChange={(e)=>patchUnit(u,{status:e.target.value})}>
                    <option value="available">available</option>
                    <option value="reserved">reserved</option>
                    <option value="sold">sold</option>
                  </select>
                </td>
                <td>
                  <select value={(u.is_active??true)?'1':'0'} onChange={(e)=>patchUnit(u,{is_active:e.target.value==='1'})}>
                    <option value="1">Да</option><option value="0">Нет</option>
                  </select>
                </td>
                <td>
                  {(() => {
                    const src = normalizeMediaUrl(u.plan_media?.thumbnail_url || u.plan_thumb || u.plan_url);
                    return src ? <img alt="" src={src} style={{width:42,height:42,borderRadius:8,objectFit:'cover'}}/> : '—';
                  })()}
                </td>
                <td>
                  <UnitPlanButton unit={u} onUpdate={() => load()} />
                </td>
                <td>
                  <UnitMediaButton unit={u} propertyType={propertyType} propertyId={propertyId}/>
                </td>
                <td style={{whiteSpace:'nowrap'}}>
                  <Btn onClick={()=>delUnit(u)}>Удалить</Btn>
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={12} style={{opacity:.7}}>Юнитов нет</td></tr>
            )}
          </tbody>
        </Table>
      </TableWrap>

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={pickFromLib} multi={true} />
    </Card>
  );
}

function UnitPlanButton({ unit, onUpdate }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Btn onClick={()=>setOpen(true)}>Изменить</Btn>
      {open && <UnitEditModal unit={unit} onClose={()=>{setOpen(false); onUpdate && onUpdate();}} />}
    </>
  );
}

function UnitEditModal({ unit, onClose }) {
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  
  const [form, setForm] = React.useState({
    title_ru: unit.title_ru || unit.title || '',
    title_en: unit.title_en || '',
    title_zh: unit.title_zh || '',
    unit_no: unit.unit_no || unit.unit_number || '',
    floor: unit.floor ?? '',
    bedrooms: unit.bedrooms ?? '',
    bathrooms: unit.bathrooms ?? '',
    area_total: unit.area_total ?? '',
    balcony_area: unit.balcony_area ?? '',
    price: unit.price ?? '',
    price_currency: unit.price_currency || unit.currency?.code || 'AED',
    status: unit.status || 'available',
    is_featured: unit.is_featured ?? false,
    is_active: unit.is_active ?? true,
    plan_media_id: unit.plan_media_id || unit.plan_media?.id || null,
  });

  const [planPreview, setPlanPreview] = React.useState(
    normalizeMediaUrl(unit.plan_media?.thumbnail_url || unit.plan_thumb || unit.plan_url) || ''
  );

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErr('');
    setOk('');
    try {
      const payload = {
        title_ru: form.title_ru || undefined,
        title_en: form.title_en || undefined,
        title_zh: form.title_zh || undefined,
        unit_no: form.unit_no || undefined,
        floor: form.floor !== '' ? Number(form.floor) : undefined,
        bedrooms: form.bedrooms !== '' ? Number(form.bedrooms) : undefined,
        bathrooms: form.bathrooms !== '' ? Number(form.bathrooms) : undefined,
        area_total: form.area_total !== '' ? Number(form.area_total) : undefined,
        balcony_area: form.balcony_area !== '' ? Number(form.balcony_area) : undefined,
        price: form.price !== '' ? Number(form.price) : undefined,
        price_currency: form.price ? form.price_currency : undefined,
        status: form.status,
        is_featured: !!form.is_featured,
        is_active: !!form.is_active,
        plan_media_id: form.plan_media_id || null,
      };
      
      await AdminAPI.updateUnit(unit.id, payload);
      setOk('Юнит обновлен');
      setTimeout(() => onClose(), 1000);
    } catch (e) {
      setErr(e?.message || 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const pickPlan = (selectedMedia) => {
    if (!selectedMedia || !selectedMedia.length) {
      setPickerOpen(false);
      return;
    }
    const media = selectedMedia[0];
    setF('plan_media_id', media.id);
    setPlanPreview(normalizeMediaUrl(media.thumbnail_url || media.url) || '');
    setPickerOpen(false);
  };

  const removePlan = () => {
    setF('plan_media_id', null);
    setPlanPreview('');
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 16
    }}>
      <div style={{
        background: 'var(--card)',
        borderRadius: 16,
        width: 'min(700px, 100%)',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--bdr)' }}>
          <h3 style={{ margin: 0 }}>Редактировать юнит #{unit.id}</h3>
        </div>

        <div style={{ 
          padding: 16, 
          overflowY: 'auto',
          flex: 1
        }}>
          {err && <div style={{ color: '#e66', marginBottom: 8 }}>{err}</div>}
          {ok && <div style={{ color: '#2a8f2a', marginBottom: 8 }}>{ok}</div>}

          <h4 style={{ marginTop: 0 }}>Основная информация</h4>
          <Row2>
            <Field>Название RU
              <input value={form.title_ru} onChange={e => setF('title_ru', e.target.value)} />
            </Field>
            <Field>Название EN
              <input value={form.title_en} onChange={e => setF('title_en', e.target.value)} />
            </Field>
          </Row2>
          <Field>Название ZH (中文)
            <input value={form.title_zh} onChange={e => setF('title_zh', e.target.value)} />
          </Field>

          <Row2>
            <Field>Номер юнита
              <input value={form.unit_no} onChange={e => setF('unit_no', e.target.value)} />
            </Field>
            <Field>Этаж
              <input type="number" value={form.floor} onChange={e => setF('floor', e.target.value)} />
            </Field>
          </Row2>

          <h4>Характеристики</h4>
          <Row2>
            <Field>Спальни
              <input type="number" value={form.bedrooms} onChange={e => setF('bedrooms', e.target.value)} />
            </Field>
            <Field>Санузлы
              <input type="number" value={form.bathrooms} onChange={e => setF('bathrooms', e.target.value)} />
            </Field>
          </Row2>
          <Row2>
            <Field>Площадь, м²
              <input type="number" value={form.area_total} onChange={e => setF('area_total', e.target.value)} />
            </Field>
            <Field>Балкон, м²
              <input type="number" value={form.balcony_area} onChange={e => setF('balcony_area', e.target.value)} />
            </Field>
          </Row2>

          <h4>Цена и статус</h4>
          <Row2>
            <Field>Цена
              <input type="number" value={form.price} onChange={e => setF('price', e.target.value)} />
            </Field>
            <Field>Валюта
              <select value={form.price_currency} onChange={e => setF('price_currency', e.target.value)}>
                <option value="RUB">RUB</option>
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
          </Row2>
          <Row2>
            <Field>Статус
              <select value={form.status} onChange={e => setF('status', e.target.value)}>
                <option value="available">available</option>
                <option value="reserved">reserved</option>
                <option value="sold">sold</option>
              </select>
            </Field>
            <Field>Активен
              <select value={form.is_active ? '1' : '0'} onChange={e => setF('is_active', e.target.value === '1')}>
                <option value="1">Да</option>
                <option value="0">Нет</option>
              </select>
            </Field>
          </Row2>

          <h4>План юнита</h4>
          {planPreview ? (
            <div style={{ marginBottom: 12 }}>
              <img
                alt="План"
                src={planPreview}
                style={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'contain',
                  borderRadius: 12,
                  border: '1px solid var(--bdr)'
                }}
              />
              {form.plan_media_id && (
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                  media_id: <b>{form.plan_media_id}</b>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              padding: 40,
              textAlign: 'center',
              opacity: 0.6,
              border: '1px dashed var(--bdr)',
              borderRadius: 12,
              marginBottom: 12
            }}>
              План не установлен
            </div>
          )}
          <Actions style={{ gap: 10 }}>
            <Btn onClick={() => setPickerOpen(true)}>
              {planPreview ? 'Изменить план' : 'Добавить план'}
            </Btn>
            {planPreview && (
              <Btn onClick={removePlan} style={{ background: '#e66', color: '#fff' }}>
                Удалить план
              </Btn>
            )}
          </Actions>
        </div>

        <div style={{
          padding: 16,
          borderTop: '1px solid var(--bdr)',
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end'
        }}>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn $kind="primary" onClick={save} disabled={busy}>
            {busy ? 'Сохранение...' : 'Сохранить'}
          </Btn>
        </div>

        <MediaPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={pickPlan}
          multi={false}
        />
      </div>
    </div>
  );
}

function UnitMediaButton({ unit, propertyType, propertyId }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Btn onClick={()=>setOpen(true)}>Управлять фото</Btn>
      {open && <UnitMediaManager unitId={unit.id} propertyType={propertyType} propertyId={propertyId} onClose={()=>setOpen(false)} />}
    </>
  );
}

// В том же файле (для простоты) модал менеджера фото:
function UnitMediaManager({ unitId, propertyType, propertyId, onClose }) {
  const [items, setItems] = React.useState([]);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const fileRef = React.useRef(null);
  const [uploadBusy, setUploadBusy] = React.useState(false);

  // Определяем API методы в зависимости от типа объекта
  const getUnitMediaMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: (unitId) => AdminAPI.listForeignUnitMedia(propertyId, unitId),
          attach: (unitId, payload) => AdminAPI.attachForeignUnitMedia(propertyId, unitId, payload),
          update: (unitId, mediaId, payload) => AdminAPI.updateForeignUnitMedia(propertyId, unitId, mediaId, payload),
          delete: (unitId, mediaId) => AdminAPI.deleteForeignUnitMedia(propertyId, unitId, mediaId),
        };
      case 'suburban':
        return {
          list: (unitId) => AdminAPI.listSuburbanUnitMedia(propertyId, unitId),
          attach: (unitId, payload) => AdminAPI.attachSuburbanUnitMedia(propertyId, unitId, payload),
          update: (unitId, mediaId, payload) => AdminAPI.updateSuburbanUnitMedia(propertyId, unitId, mediaId, payload),
          delete: (unitId, mediaId) => AdminAPI.deleteSuburbanUnitMedia(propertyId, unitId, mediaId),
        };
      case 'commercial-rent':
        return {
          list: (unitId) => AdminAPI.listCommercialRentUnitMedia(propertyId, unitId),
          attach: (unitId, payload) => AdminAPI.attachCommercialRentUnitMedia(propertyId, unitId, payload),
          update: (unitId, mediaId, payload) => AdminAPI.updateCommercialRentUnitMedia(propertyId, unitId, mediaId, payload),
          delete: (unitId, mediaId) => AdminAPI.deleteCommercialRentUnitMedia(propertyId, unitId, mediaId),
        };
      case 'parking':
        return {
          list: (unitId) => AdminAPI.listParkingUnitMedia(propertyId, unitId),
          attach: (unitId, payload) => AdminAPI.attachParkingUnitMedia(propertyId, unitId, payload),
          update: (unitId, mediaId, payload) => AdminAPI.updateParkingUnitMedia(propertyId, unitId, mediaId, payload),
          delete: (unitId, mediaId) => AdminAPI.deleteParkingUnitMedia(propertyId, unitId, mediaId),
        };
      default:
        return {
          list: AdminAPI.listUnitMedia,
          attach: AdminAPI.attachUnitMedia,
          update: AdminAPI.updateUnitMedia,
          delete: AdminAPI.deleteUnitMedia,
        };
    }
  };

  const unitMediaAPI = React.useMemo(() => getUnitMediaMethods(), [propertyType, propertyId]);

  const load = React.useCallback(async ()=>{
    setErr(''); setOk('');
    try{
      const d = await unitMediaAPI.list(unitId);
      setItems(d?.images || []);
    }catch(e){ setErr(e?.message || 'Не удалось загрузить фото юнита'); }
  }, [unitId, unitMediaAPI]);
  
  React.useEffect(()=>{ load(); }, [load]);

  const attachByFiles = async (fileList)=>{
    const files = Array.from(fileList||[]).filter(Boolean);
    if(!files.length) return;
    setUploadBusy(true);
    try{
      let order = items.length||0;
      for(let i=0;i<files.length;i++){
        const up = await AdminAPI.uploadMedia(files[i]);
        const f  = up?.file || up;
        if(!f?.id) throw new Error('Медиафайл не вернул id');
        await unitMediaAPI.attach(unitId, { media_id: f.id, sort_order: order++ });
      }
      setOk('Фото добавлены');
      await load();
    }catch(e){ setErr(e?.message || 'Не удалось добавить фото'); }
    finally{ setUploadBusy(false); if(fileRef.current) fileRef.current.value=''; }
  };
  
  const attachFromLib = async (list)=>{
    const picked = Array.isArray(list) ? list : [];
    if(!picked.length) return;
    try{
      let order = items.length||0;
      for(let i=0;i<picked.length;i++){
        await unitMediaAPI.attach(unitId, { media_id: picked[i].id, sort_order: order++ });
      }
      setOk('Фото добавлены');
      await load();
    }catch(e){ setErr(e?.message || 'Не удалось добавить фото'); }
  };
  
  const setMain = async (link)=>{ 
    try{ 
      await unitMediaAPI.update(unitId, link.id, { is_main: true }); 
      await load(); 
    } catch(e){ alert(e?.message || 'Не удалось пометить главным'); } 
  };
  
  const setOrder = async (link, n)=>{ 
    try{ 
      await unitMediaAPI.update(unitId, link.id, { sort_order: Number(n)||0 }); 
      await load(); 
    } catch(e){ alert(e?.message || 'Не удалось сохранить порядок'); } 
  };
  
  const del = async (link)=>{ 
    if(!window.confirm('Удалить фото?')) return; 
    try{ 
      await unitMediaAPI.delete(unitId, link.id); 
      await load(); 
    } catch(e){ alert(e?.message || 'Не удалось удалить'); } 
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
    }}>
      <div style={{background:'var(--card)', padding:16, borderRadius:16, width:'min(900px, 94vw)'}}>
        <h3>Фото юнита #{unitId}</h3>
        {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
        {ok  && <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))', gap:12, marginTop:8}}>
          {items.map(it=>(
            <div key={it.id} style={{border:'1px solid var(--bdr)', borderRadius:12, padding:8}}>
              {normalizeMediaUrl(it.thumbnail_url || it.url)
                ? <img alt="" src={normalizeMediaUrl(it.thumbnail_url || it.url)} style={{width:'100%', aspectRatio:'1/1', objectFit:'cover', borderRadius:8}}/>
                : <div style={{height:120, display:'grid', placeItems:'center', opacity:.6}}>нет превью</div>}
              <div style={{display:'flex', gap:6, alignItems:'center', marginTop:6}}>
                <Btn onClick={()=>setMain(it)}>{it.is_main ? 'Главное' : 'Сделать главным'}</Btn>
                <input type="number" value={it.sort_order ?? 0} onChange={(e)=>setOrder(it, e.target.value)} style={{width:70}}/>
                <Btn onClick={()=>del(it)}>Удалить</Btn>
              </div>
            </div>
          ))}
          {!items.length && <div style={{opacity:.7}}>Нет фото</div>}
        </div>

        <Actions style={{marginTop:12, gap:10, flexWrap:'wrap'}}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{display:'none'}} onChange={(e)=>attachByFiles(e.target.files)}/>
          <Btn onClick={()=>fileRef.current?.click()} disabled={uploadBusy}>{uploadBusy?'Загрузка…':'Загрузить выбранные'}</Btn>
          <Btn onClick={()=>setPickerOpen(true)} disabled={uploadBusy}>Выбрать из медиатеки</Btn>
          <div style={{flex:1}}/>
          <Btn onClick={onClose}>Закрыть</Btn>
        </Actions>

        <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={attachFromLib} multi={true} />
      </div>
    </div>
  );
}
